
import React, { useState, startTransition } from 'react';
import { UnifiedDialPad } from './UnifiedDialPad';
import { ContactBookSelection } from '@/components/shared/ContactBook';
import { Button } from '@/components/ui/button';
import { Phone, Mic, MicOff, Volume2, VolumeX, Clock, Hash, Keyboard, User, Building2 } from 'lucide-react';
import { useTwilioContext } from '@/context/TwilioContext';
import { cn } from '@/lib/utils';

interface TwilioDialerContentProps {
  className?: string;
}

export const TwilioDialerContent = ({ className }: TwilioDialerContentProps) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [showDialpad, setShowDialpad] = useState(false);
  const [currentContact, setCurrentContact] = useState<ContactBookSelection | null>(null);
  
  const { 
    makeCall, 
    endCall,
    toggleMute,
    sendDTMF,
    callInProgress,
    callStatus,
    formattedDuration,
    isMuted
  } = useTwilioContext();

  const handleCall = async (number: string, contactInfo?: ContactBookSelection) => {
    try {
      setPhoneNumber(number);
      if (contactInfo) {
        setCurrentContact(contactInfo);
        console.log('📞 Calling contact:', contactInfo.contact.full_name, 'at', contactInfo.contact.lead_name);
      }
      // Extract contact ID if a contact is selected
      const contactId = contactInfo?.contact.contact_id;
      console.log('👤 Contact ID for call:', contactId || 'none');
      
      await makeCall(number, contactId);
    } catch (error) {
      console.error('Call failed:', error);
    }
  };

  const handleContactSelect = (contact: ContactBookSelection) => {
    setCurrentContact(contact);
  };

  const handleEndCall = async () => {
    try {
      await endCall();
      setPhoneNumber('');
      setCurrentContact(null); // Clear contact when call ends
      setShowDialpad(false); // Hide dialpad when call ends
    } catch (error) {
      console.error('End call failed:', error);
    }
  };

  const handleMuteToggle = async () => {
    try {
      await toggleMute(!isMuted);
    } catch (error) {
      console.error('Mute toggle failed:', error);
    }
  };

  const handleSpeakerToggle = () => {
    startTransition(() => {
      setIsSpeakerOn(!isSpeakerOn);
    });
  };

  const handleSendDTMF = async (digit: string) => {
    await sendDTMF(digit);
  };

  const toggleDialpad = () => {
    setShowDialpad(!showDialpad);
  };

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <div className="flex-1 flex flex-col justify-center p-4">
        
        {/* Call in progress - Show call controls */}
        {callInProgress && !showDialpad && (
          <div className="flex flex-col items-center space-y-6">
            {/* Call status display */}
            <div className="flex flex-col items-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                {currentContact ? <User size={32} className="text-primary" /> : <Phone size={32} className="text-primary" />}
              </div>
              
              <div className="text-center">
                {/* Contact Info or Phone Number */}
                {currentContact ? (
                  <div className="space-y-1">
                    <p className="font-medium text-base">{currentContact.contact.full_name}</p>
                    <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                      <Building2 className="h-3 w-3" />
                      <span>{currentContact.contact.lead_name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">{phoneNumber}</p>
                  </div>
                ) : (
                  <p className="font-medium text-base">{phoneNumber}</p>
                )}
                
                <div className={`flex items-center justify-center space-x-2 ${
                  callStatus === 'in-progress' ? 'text-green-500' : 'text-muted-foreground'
                }`}>
                  <span className="text-sm">
                    {callStatus === 'in-progress' ? 'Call in progress' : callStatus}
                  </span>
                  {callStatus === 'in-progress' && (
                    <>
                      <span>•</span>
                      <span className="flex items-center text-sm">
                        <Clock className="h-3 w-3 mr-1" />
                        {formattedDuration}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Call control buttons */}
            <div className="flex space-x-3">
              <Button 
                size="icon" 
                variant={isMuted ? "default" : "outline"}
                className={cn(
                  "rounded-full h-10 w-10",
                  isMuted && "bg-red-500 hover:bg-red-600"
                )}
                onClick={handleMuteToggle}
              >
                {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </Button>

              <Button 
                size="icon" 
                variant={showDialpad ? "default" : "outline"}
                className="rounded-full h-10 w-10"
                onClick={toggleDialpad}
              >
                <Keyboard className="h-5 w-5" />
              </Button>
              
              <Button 
                onClick={handleEndCall}
                size="icon" 
                variant="destructive" 
                className="rounded-full h-12 w-12 bg-red-500 hover:bg-red-600"
              >
                <Phone className="h-6 w-6" />
              </Button>
              
              <Button 
                size="icon" 
                variant={isSpeakerOn ? "outline" : "default"}
                className={cn(
                  "rounded-full h-10 w-10",
                  !isSpeakerOn && "bg-slate-600 hover:bg-slate-700"
                )}
                onClick={handleSpeakerToggle}
              >
                {isSpeakerOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>
            </div>
            
            <div className="text-xs text-muted-foreground">
              Call is made through Twilio service
            </div>
          </div>
        )}

        {/* Show dialpad for new calls OR when dialpad is toggled during call */}
        {(!callInProgress || showDialpad) && (
          <div className="flex flex-col items-center space-y-4">
            {/* Mode indicator during calls */}
            {callInProgress && showDialpad && (
              <div className="flex items-center justify-between w-full max-w-xs">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleDialpad}
                  className="text-muted-foreground"
                >
                  ← Back to call
                </Button>
                <span className="text-sm text-muted-foreground">Enter Extension</span>
                <div></div> {/* Spacer for centering */}
              </div>
            )}
            
            <UnifiedDialPad
              mode={callInProgress ? 'dtmf' : 'call'}
              onCall={handleCall}
              onSendDTMF={handleSendDTMF}
              onContactSelect={handleContactSelect}
              disabled={false}
              phoneNumber={callInProgress ? undefined : phoneNumber}
              onPhoneNumberChange={callInProgress ? undefined : setPhoneNumber}
            />
          </div>
        )}
      </div>
    </div>
  );
};
